import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

const SplitBillModal = ({ tab, onClose, onCreateSplits, formatCurrency }) => {
  const [mode, setMode] = useState('equal');
  const [equalParts, setEqualParts] = useState(2);
  const [itemAssignments, setItemAssignments] = useState([]);
  const [amounts, setAmounts] = useState(['']);

  useEffect(() => {
    setItemAssignments(tab.items?.map(() => 0) || []);
  }, [tab]);

  const orderTotal = (tab.subtotal || 0) + (tab.tax || 0) - (tab.discount || 0);

  const buildSplits = () => {
    const splits = [];
    if (mode === 'equal') {
      const base = orderTotal / equalParts;
      let remaining = orderTotal;
      for (let i = 0; i < equalParts; i++) {
        const isLast = i === equalParts - 1;
        const total = isLast ? remaining : Math.round(base * 100) / 100;
        remaining -= total;
        const ratio = (tab.subtotal || 0) > 0 ? total / orderTotal : 0;
        splits.push({
          splitId: `split_${Date.now()}_${i}`,
          label: `Check ${i + 1}`,
          type: 'equal',
          items: tab.items?.map(it => ({ ...it })) || [],
          subtotal: (tab.subtotal || 0) * ratio,
          tax: (tab.tax || 0) * ratio,
          discount: (tab.discount || 0) * ratio,
          total,
          tip: 0,
          isPaid: false,
          paymentMethod: null,
        });
      }
    } else if (mode === 'items') {
      const checks = [[], [], []];
      const subs = [0, 0, 0];
      tab.items?.forEach((it, idx) => {
        const checkIdx = Math.min(2, itemAssignments[idx] || 0);
        checks[checkIdx].push({ ...it });
        subs[checkIdx] += it.total || 0;
      });
      for (let i = 0; i < 3; i++) {
        if (checks[i].length === 0) continue;
        const ratio = (tab.subtotal || 0) > 0 ? subs[i] / tab.subtotal : 0;
        splits.push({
          splitId: `split_${Date.now()}_${i}`,
          label: `Check ${i + 1}`,
          type: 'items',
          items: checks[i],
          subtotal: subs[i],
          tax: (tab.tax || 0) * ratio,
          discount: (tab.discount || 0) * ratio,
          total: subs[i] + (tab.tax || 0) * ratio - (tab.discount || 0) * ratio,
          tip: 0,
          isPaid: false,
          paymentMethod: null,
        });
      }
    } else if (mode === 'amount') {
      const vals = amounts.map(a => parseFloat(a) || 0).filter(v => v > 0);
      const totalAssigned = vals.reduce((s, v) => s + v, 0);
      if (Math.abs(totalAssigned - orderTotal) > 0.02) {
        Swal.fire('Error', `Amounts must sum to ${formatCurrency(orderTotal)}. Current: ${formatCurrency(totalAssigned)}`, 'error');
        return null;
      }
      for (let i = 0; i < vals.length; i++) {
        const ratio = (tab.subtotal || 0) > 0 ? vals[i] / orderTotal : 0;
        splits.push({
          splitId: `split_${Date.now()}_${i}`,
          label: `Check ${i + 1}`,
          type: 'amount',
          items: tab.items?.map(it => ({ ...it })) || [],
          subtotal: (tab.subtotal || 0) * ratio,
          tax: (tab.tax || 0) * ratio,
          discount: (tab.discount || 0) * ratio,
          total: vals[i],
          tip: 0,
          isPaid: false,
          paymentMethod: null,
        });
      }
    }
    return splits;
  };

  const handleConfirm = () => {
    const splits = buildSplits();
    if (!splits || splits.length === 0) return;
    onCreateSplits(splits);
    onClose();
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content" style={{ borderRadius: '20px', border: 'none' }}>
          <div className="modal-header border-0 pb-0">
            <h5 className="modal-title font-weight-bold">Split Bill</h5>
            <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
          </div>
          <div className="modal-body">
            <div className="d-flex mb-3" style={{ gap: 8 }}>
              {['equal', 'items', 'amount'].map(m => (
                <button key={m} className={`btn btn-sm flex-fill ${mode === m ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ borderRadius: 8, fontSize: '0.8rem', textTransform: 'capitalize',
                    backgroundColor: mode === m ? '#ff6b35' : undefined,
                    borderColor: mode === m ? '#ff6b35' : undefined }}
                  onClick={() => setMode(m)}>
                  {m === 'equal' ? 'Equal' : m === 'items' ? 'By Items' : 'By Amount'}
                </button>
              ))}
            </div>

            {mode === 'equal' && (
              <div>
                <label className="font-weight-bold" style={{ fontSize: '0.85rem' }}>Number of Checks</label>
                <div className="d-flex align-items-center mb-3">
                  <button className="btn btn-light" onClick={() => setEqualParts(Math.max(2, equalParts - 1))} disabled={equalParts <= 2}><i className="fas fa-minus"></i></button>
                  <span className="mx-3 font-weight-bold" style={{ fontSize: '1.2rem' }}>{equalParts}</span>
                  <button className="btn btn-light" onClick={() => setEqualParts(Math.min(8, equalParts + 1))} disabled={equalParts >= 8}><i className="fas fa-plus"></i></button>
                </div>
                <p className="text-muted" style={{ fontSize: '0.8rem' }}>~{formatCurrency(orderTotal / equalParts)} per check</p>
              </div>
            )}

            {mode === 'items' && (
              <div>
                <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>Assign items to checks (up to 3)</p>
                {tab.items?.map((item, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-center mb-2 p-2" style={{ background: '#f8f9fa', borderRadius: 8 }}>
                    <span style={{ fontSize: '0.85rem' }}>{item.qty}× {item.productName}</span>
                    <div className="btn-group btn-group-sm">
                      {[0, 1, 2].map(ci => (
                        <button key={ci} className={`btn ${itemAssignments[idx] === ci ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ fontSize: '0.7rem',
                            backgroundColor: itemAssignments[idx] === ci ? '#ff6b35' : undefined,
                            borderColor: itemAssignments[idx] === ci ? '#ff6b35' : undefined }}
                          onClick={() => {
                            const next = [...itemAssignments];
                            next[idx] = ci;
                            setItemAssignments(next);
                          }}>
                          {ci + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {mode === 'amount' && (
              <div>
                <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>Enter amount for each check</p>
                {amounts.map((amt, idx) => (
                  <div key={idx} className="d-flex align-items-center mb-2">
                    <span className="mr-2" style={{ fontSize: '0.85rem', minWidth: 60 }}>Check {idx + 1}</span>
                    <div className="input-group input-group-sm">
                      <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                      <input type="number" step="0.01" className="form-control" value={amt}
                        onChange={(e) => { const next = [...amounts]; next[idx] = e.target.value; setAmounts(next); }} />
                    </div>
                    {amounts.length > 1 && (
                      <button className="btn btn-sm btn-link text-danger ml-2" onClick={() => setAmounts(prev => prev.filter((_, i) => i !== idx))}>
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    )}
                  </div>
                ))}
                {amounts.length < 8 && (
                  <button className="btn btn-sm btn-outline-primary" onClick={() => setAmounts(prev => [...prev, ''])}>
                    <i className="fas fa-plus mr-1"></i>Add Check
                  </button>
                )}
                <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                  Assigned: {formatCurrency(amounts.reduce((s, a) => s + (parseFloat(a) || 0), 0))} / {formatCurrency(orderTotal)}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer border-0 pt-0">
            <button className="btn btn-lg btn-light" style={{ borderRadius: '12px' }} onClick={onClose}>Cancel</button>
            <button className="btn btn-lg btn-primary font-weight-bold" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', borderRadius: '12px' }} onClick={handleConfirm}>
              <i className="fas fa-check mr-2"></i>Create Splits
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SplitBillModal;
